import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const IntakeForm = ({ formData, onChange, errors }) => {
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);

  const roleOptions = [
    { value: '', label: 'Select your role' },
    { value: 'surgeon', label: 'Surgeon' },
    { value: 'practice_marketer', label: 'Practice Marketing Team' },
    { value: 'admin_staff', label: 'Administrative Staff' }
  ];

  const marketingGoalOptions = [
    { value: '', label: 'Select primary goal' },
    { value: 'increase_awareness', label: 'Increase Brand Awareness' },
    { value: 'patient_education', label: 'Patient Education' },
    { value: 'practice_growth', label: 'Practice Growth' },
    { value: 'technology_adoption', label: 'Technology Adoption' },
    { value: 'community_engagement', label: 'Community Engagement' }
  ];

  const currentEffortOptions = [
    { value: '', label: 'Select current effort level' },
    { value: 'none', label: 'Not Currently Marketing' },
    { value: 'minimal', label: 'Minimal (1-2 posts/month)' },
    { value: 'moderate', label: 'Moderate (1-2 posts/week)' },
    { value: 'active', label: 'Active (3+ posts/week)' },
    { value: 'very_active', label: 'Very Active (Daily posting)' }
  ];

  const handleInputChange = (field, value) => {
    onChange({ ...formData, [field]: value });
  };

  const handleCheckboxChange = (field, checked) => {
    onChange({ ...formData, [field]: checked });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Input
          label="Full Name"
          type="text"
          placeholder="Dr. Sarah Mitchell"
          value={formData?.fullName || ''}
          onChange={(e) => handleInputChange('fullName', e?.target?.value)}
          error={errors?.fullName}
          required
        />

        <Input
          label="Email Address"
          type="email"
          placeholder="sarah.mitchell@example.com"
          value={formData?.email || ''}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          required
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Input
          label="Phone Number"
          type="tel"
          placeholder="(555) 123-4567"
          value={formData?.phone || ''}
          onChange={(e) => handleInputChange('phone', e?.target?.value)}
          error={errors?.phone}
          required
        />

        <Select
          label="Your Role"
          options={roleOptions}
          value={formData?.role || ''}
          onChange={(value) => handleInputChange('role', value)}
          error={errors?.role}
          required
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Input
          label="Practice Name"
          type="text"
          placeholder="Advanced Surgical Center"
          value={formData?.practiceName || ''}
          onChange={(e) => handleInputChange('practiceName', e?.target?.value)}
          error={errors?.practiceName}
          required
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="City"
            type="text"
            placeholder="Boston"
            value={formData?.city || ''}
            onChange={(e) => handleInputChange('city', e?.target?.value)}
            error={errors?.city}
            required
          />

          <Input
            label="State"
            type="text"
            placeholder="MA"
            value={formData?.state || ''}
            onChange={(e) => handleInputChange('state', e?.target?.value)}
            error={errors?.state}
            required
          />
        </div>
      </div>
      <div className="pt-4 border-t border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Target" size={20} color="var(--color-primary)" />
          Marketing Information
        </h3>

        <div className="space-y-4 md:space-y-6">
          <Select
            label="Primary Marketing Goal"
            description="What would you like to achieve with marketing support?"
            options={marketingGoalOptions}
            value={formData?.marketingGoal || ''}
            onChange={(value) => handleInputChange('marketingGoal', value)}
            error={errors?.marketingGoal}
            required
          />

          <Select
            label="Current Marketing Effort"
            description="How active is your current social media presence?"
            options={currentEffortOptions}
            value={formData?.currentEffort || ''}
            onChange={(value) => handleInputChange('currentEffort', value)}
            error={errors?.currentEffort}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Specific Challenges or Questions
              <span className="text-destructive ml-1">*</span>
            </label>
            <textarea
              placeholder="Please describe any specific marketing challenges you're facing or questions you'd like to discuss during the consultation..."
              value={formData?.challenges || ''}
              onChange={(e) => handleInputChange('challenges', e?.target?.value)}
              rows={4}
              className={`w-full px-4 py-3 rounded-lg border ${
                errors?.challenges ? 'border-destructive' : 'border-input'
              } bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-250 resize-none`}
              required
            />
            {errors?.challenges && (
              <p className="mt-2 text-sm text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={14} />
                {errors?.challenges}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={() => setShowAdditionalInfo(!showAdditionalInfo)}
            className="flex items-center gap-2 text-sm transition-colors duration-250"
            style={{ color: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 1)))' }}
          >
            <Icon
              name={showAdditionalInfo ? 'ChevronUp' : 'ChevronDown'}
              size={16}
            />
            {showAdditionalInfo ? 'Hide' : 'Show'} Additional Information
          </button>

          {showAdditionalInfo && (
            <div className="space-y-4 pl-4" style={{ borderLeftWidth: '2px', borderLeftColor: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 0.2)))' }}>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Current Social Media Platforms
                </label>
                <div className="space-y-2">
                  <Checkbox
                    label="LinkedIn"
                    checked={formData?.platforms?.linkedin || false}
                    onChange={(e) =>
                      handleInputChange('platforms', {
                        ...formData?.platforms,
                        linkedin: e?.target?.checked
                      })
                    }
                  />
                  <Checkbox
                    label="Facebook"
                    checked={formData?.platforms?.facebook || false}
                    onChange={(e) =>
                      handleInputChange('platforms', {
                        ...formData?.platforms,
                        facebook: e?.target?.checked
                      })
                    }
                  />
                  <Checkbox
                    label="Instagram"
                    checked={formData?.platforms?.instagram || false}
                    onChange={(e) =>
                      handleInputChange('platforms', {
                        ...formData?.platforms,
                        instagram: e?.target?.checked
                      })
                    }
                  />
                  <Checkbox
                    label="Twitter/X"
                    checked={formData?.platforms?.twitter || false}
                    onChange={(e) =>
                      handleInputChange('platforms', {
                        ...formData?.platforms,
                        twitter: e?.target?.checked
                      })
                    }
                  />
                </div>
              </div>

              <Input
                label="Website URL (Optional)"
                type="url"
                placeholder="https://www.yourpractice.com"
                value={formData?.website || ''}
                onChange={(e) => handleInputChange('website', e?.target?.value)}
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  placeholder="Any other information you'd like to share..."
                  value={formData?.additionalNotes || ''}
                  onChange={(e) => handleInputChange('additionalNotes', e?.target?.value)}
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-250 resize-none"
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="pt-4">
        <Checkbox
          label="I agree to receive marketing support communications and appointment reminders"
          description="We'll only contact you regarding your scheduled consultation and related marketing support"
          checked={formData?.agreedToContact || false}
          onChange={(e) => handleCheckboxChange('agreedToContact', e?.target?.checked)}
          error={errors?.agreedToContact}
          required
        />
      </div>
    </div>
  );
};

export default IntakeForm;